import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Target, Package, BookOpen, MessageSquareQuote, ListOrdered, HelpCircle,
  Sparkles, Layers, Eye, Home, AlertTriangle,
} from "lucide-react";

// Renders one English + Spanish pair with clear separation.
function Bi({ en, es }) {
  const hasEn = en && en.trim();
  const hasEs = es && es.trim();
  return (
    <div className="space-y-1">
      {hasEn && <p className="text-foreground leading-relaxed">{en}</p>}
      {hasEs && (
        <p className="text-accent italic leading-relaxed">{es}</p>
      )}
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
          <Icon className="h-4 w-4 text-primary" strokeWidth={2} />
        </span>
        <h3 className="font-heading font-bold text-lg">{title}</h3>
      </div>
      <div className="pl-10">{children}</div>
    </section>
  );
}

export function ActivityDisplay({ content, params, t }) {
  if (!content) return null;
  const c = content;
  const p = params || {};

  const diffMap = [
    ["additional_support", t("activity.diffSupport")],
    ["beginning_english", t("activity.diffEnglish")],
    ["beginning_spanish", t("activity.diffSpanish")],
    ["additional_challenge", t("activity.diffChallenge")],
  ];

  const meta = [p.age_group, ...(p.domains || []), p.activity_type, p.duration].filter(Boolean);

  return (
    <article className="print-area space-y-8">
      <header className="space-y-3">
        <p className="text-xs font-semibold uppercase tracking-widest text-secondary hidden print:block">
          Brilla Bilingual Classroom Activity Coach
        </p>
        <div>
          {c.title_en && <h2 className="font-heading font-extrabold text-3xl tracking-tight">{c.title_en}</h2>}
          {c.title_es && <p className="font-heading font-bold text-xl text-accent italic mt-1">{c.title_es}</p>}
        </div>
        {meta.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {meta.map((m, i) => (
              <Badge key={i} variant="secondary" className="rounded-full bg-muted text-muted-foreground font-medium">{m}</Badge>
            ))}
          </div>
        )}
      </header>

      <Separator />

      {(c.objective_en || c.objective_es) && (
        <Section icon={Target} title={`${t("activity.objective")} / Objetivo`}>
          <Bi en={c.objective_en} es={c.objective_es} />
        </Section>
      )}

      {((c.materials_en && c.materials_en.length) || (c.materials_es && c.materials_es.length)) && (
        <Section icon={Package} title={`${t("activity.materials")} / Materiales`}>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1">
            <ul className="list-disc pl-5 space-y-1">
              {(c.materials_en || []).map((m, i) => <li key={i} className="text-foreground">{m}</li>)}
            </ul>
            <ul className="list-disc pl-5 space-y-1">
              {(c.materials_es || []).map((m, i) => <li key={i} className="text-accent italic">{m}</li>)}
            </ul>
          </div>
        </Section>
      )}

      {c.vocabulary && c.vocabulary.length > 0 && (
        <Section icon={BookOpen} title={`${t("activity.vocabulary")} / Vocabulario clave`}>
          <div className="flex flex-wrap gap-2">
            {c.vocabulary.map((v, i) => (
              <div key={i} className="rounded-xl border border-border bg-card px-3 py-2">
                <span className="font-semibold text-foreground">{v.en}</span>
                {v.en && v.es && <span className="text-muted-foreground mx-2">—</span>}
                <span className="text-accent italic">{v.es}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {c.teacher_language && c.teacher_language.length > 0 && (
        <Section icon={MessageSquareQuote} title={`${t("activity.teacherLanguage")} / Lenguaje del maestro`}>
          <p className="text-sm font-semibold text-muted-foreground mb-2">{t("activity.teacherSays")} / El maestro dice</p>
          <div className="space-y-3">
            {c.teacher_language.map((tl, i) => (
              <div key={i} className="border-l-2 border-secondary pl-4">
                {tl.en && <p className="text-foreground">“{tl.en}”</p>}
                {tl.es && <p className="text-accent italic">“{tl.es}”</p>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {c.instructions && c.instructions.length > 0 && (
        <Section icon={ListOrdered} title={`${t("activity.instructions")}`}>
          <ol className="space-y-3">
            {c.instructions.map((s, i) => (
              <li key={i} className="flex gap-3">
                <span className="h-6 w-6 shrink-0 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center">{i + 1}</span>
                <div><Bi en={s.en} es={s.es} /></div>
              </li>
            ))}
          </ol>
        </Section>
      )}

      {c.higher_order_questions && c.higher_order_questions.length > 0 && (
        <Section icon={HelpCircle} title={`${t("activity.questions")}`}>
          <ul className="space-y-3">
            {c.higher_order_questions.map((q, i) => (
              <li key={i}><Bi en={q.en} es={q.es} /></li>
            ))}
          </ul>
        </Section>
      )}

      {c.dll_strategies && c.dll_strategies.length > 0 && (
        <Section icon={Sparkles} title={`${t("activity.dll")}`}>
          <ul className="grid sm:grid-cols-2 gap-2">
            {c.dll_strategies.map((d, i) => (
              <li key={i} className="rounded-xl bg-muted px-3 py-2">
                <Bi en={d.en} es={d.es} />
              </li>
            ))}
          </ul>
        </Section>
      )}

      {c.differentiation && (
        <Section icon={Layers} title={`${t("activity.differentiation")} / Diferenciación`}>
          <div className="grid sm:grid-cols-2 gap-3">
            {diffMap.map(([key, label]) => {
              const d = c.differentiation[key];
              if (!d || (!d.en && !d.es)) return null;
              return (
                <div key={key} className="rounded-xl border border-border p-3">
                  <p className="font-semibold text-sm text-secondary mb-1">{label}</p>
                  <Bi en={d.en} es={d.es} />
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {(c.observation_en || c.observation_es) && (
        <Section icon={Eye} title={`${t("activity.observation")}`}>
          <Bi en={c.observation_en} es={c.observation_es} />
        </Section>
      )}

      {(c.family_connection_en || c.family_connection_es) && (
        <Section icon={Home} title={`${t("activity.family")} / Conexión familiar`}>
          <div className="rounded-2xl bg-secondary/10 border border-secondary/20 p-4">
            <Bi en={c.family_connection_en} es={c.family_connection_es} />
          </div>
        </Section>
      )}

      <div className="flex items-start gap-2 rounded-xl bg-muted p-4 text-sm text-muted-foreground">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-secondary" />
        <p>{t("activity.disclaimer")}</p>
      </div>
    </article>
  );
}
